import { Weight, WeightUnits } from "./Details";
import { Macronutrient } from "./MacroNutrient";
import { Util } from "./Util";

export enum ReachGoal {
  LooseWeight = 0,
  BuildMuscle = 1,
  BodyRecomp = 2,
  AthleticPerformace = 3,
  ImproveHealth = 4,
}

export enum Diet {
  Anything = 0,
  Mediterranean = 1,
  Paleo = 2,
  Vegetarian = 3,
  Ketogenic = 4,
  PlantBased = 5,
}

export enum Activity {
  VeryLight = 0,
  Light = 1,
  Moderate = 2,
  Heavy = 3,
}

export enum Workout {
  VeryLight = 0,
  Light = 1,
  Moderate = 2,
  Intense = 3,
  VeryIntense = 4,
}

export class Goals {
  reachGoal?: ReachGoal;
  targetWeight?: Weight;
  targetDate?: string;
  eatingStyle?: Diet;
  macroNutrient?: Macronutrient;
  mealCount?: number;
  activity?: Activity;
  workout?: Workout;

  setReachGoal(index: number): boolean {
    switch (index) {
      case 0:
        this.reachGoal = ReachGoal.LooseWeight;
        return true;
      case 1:
        this.reachGoal = ReachGoal.BuildMuscle;
        return true;
      case 2:
        this.reachGoal = ReachGoal.BodyRecomp;
        return true;
      case 3:
        this.reachGoal = ReachGoal.AthleticPerformace;
        return true;
      case 4:
        this.reachGoal = ReachGoal.ImproveHealth;
        return true;
      default:
        return false;
    }
  }

  getReachGoal(): string {
    switch (this.reachGoal) {
      case ReachGoal.LooseWeight:
        return "Losing Weight";
      case ReachGoal.BuildMuscle:
        return "Building Muscle";
      case ReachGoal.AthleticPerformace:
        this.reachGoal = ReachGoal.AthleticPerformace;
        return "Athletic Performance";
      case ReachGoal.BodyRecomp:
        return "Body Recomposition";
      case ReachGoal.ImproveHealth:
        return "Improving Health";
      default:
        return "";
    }
  }
  getBodyGoalAdjustment(): number {
    switch (this.reachGoal!) {
      case ReachGoal.LooseWeight:
        return 20;
      case ReachGoal.BuildMuscle:
        return 10;
      case ReachGoal.BodyRecomp:
        return 7.5;
      default:
        return 0;
    }
  }

  setEatingStyle(index: number): boolean {
    switch (index) {
      case 0:
        this.eatingStyle = Diet.Anything;
        return true;
      case 1:
        this.eatingStyle = Diet.Mediterranean;
        return true;
      case 2:
        this.eatingStyle = Diet.Paleo;
        return true;
      case 3:
        this.eatingStyle = Diet.Vegetarian;
        return true;
      case 4:
        this.eatingStyle = Diet.Ketogenic;
        return true;
      case 5:
        this.eatingStyle = Diet.PlantBased;
        return true;
      default:
        return false;
    }
  }

  getEatingStyle(): string {
    if (typeof this.eatingStyle === "undefined") {
      return "";
    }
    switch (this.eatingStyle) {
      case Diet.Anything:
        return "Anything";
      case Diet.Mediterranean:
        return "Mediterranean";
      case Diet.Paleo:
        return "Paleo";
      case Diet.Vegetarian:
        return "Vegetarian";
      case Diet.Ketogenic:
        return "Ketogenic";
      case Diet.PlantBased:
        return "Fully Plant-Based";
    }
  }

  setTargetWeight(weight: Weight): boolean {
    if (typeof weight === "undefined") {
      return false;
    }

    if (Util.validateWeight(weight)) {
      if (
        typeof weight.weightKg !== "undefined" &&
        weight.selectedUnit === WeightUnits.kg
      ) {
        this.targetWeight = {
          weightKg: weight.weightKg,
          weightLbs: Util.convertKgToLbs(weight.weightKg),
          selectedUnit: weight.selectedUnit,
        };
        return true;
      } else if (
        typeof weight.weightLbs !== "undefined" &&
        weight.selectedUnit === WeightUnits.lbs
      ) {
        this.targetWeight = {
          weightKg: Util.convertLbsToKg(weight.weightLbs),
          weightLbs: weight.weightLbs,
          selectedUnit: weight.selectedUnit,
        };
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  getTargetWeight(): string {
    return Util.getWeight(this.targetWeight!);
  }

  setTargetDate(targetDate: string): boolean {
    this.targetDate = targetDate;
    return true;
  }

  setMealCount(mealCount: number): boolean {
    if (mealCount < 1 || mealCount > 8) {
      return false;
    } else {
      this.mealCount = mealCount;
      return true;
    }
  }

  setActivity(index: number): boolean {
    switch (index) {
      case 0:
        this.activity = Activity.VeryLight;
        return true;
      case 1:
        this.activity = Activity.Light;
        return true;
      case 2:
        this.activity = Activity.Moderate;
        return true;
      case 3:
        this.activity = Activity.Heavy;
        return true;
      default:
        return false;
    }
  }

  setWorkout(index: number): boolean {
    switch (index) {
      case 0:
        this.workout = Workout.VeryLight;
        return true;
      case 1:
        this.workout = Workout.Light;
        return true;
      case 2:
        this.workout = Workout.Moderate;
        return true;
      case 3:
        this.workout = Workout.Intense;
        return true;
      case 4:
        this.workout = Workout.VeryIntense;
        return true;
      default:
        return false;
    }
  }

  getRestingActivityLevel(): number {
    switch (this.activity!) {
      case Activity.VeryLight:
        return 0;
      case Activity.Light:
        return 1;
      case Activity.Moderate:
        return 2;
      case Activity.Heavy:
        return 4;
    }
  }

  getWorkoutActivityLevel(): number {
    switch (this.workout!) {
      case Workout.VeryLight:
        return 0;
      case Workout.Light:
        return 1;
      case Workout.Moderate:
        return 2;
      case Workout.Intense:
        return 3;
      case Workout.VeryIntense:
        return 4;
    }
  }
  getActivityMultiplier(): number {
    const totalActivityLevel =
      this.getRestingActivityLevel() + this.getWorkoutActivityLevel();
    if (totalActivityLevel >= 8) {
      return 1.9;
    } else if (totalActivityLevel > 3) {
      return 1.725;
    } else if (totalActivityLevel > 2) {
      return 1.55;
    } else if (totalActivityLevel > 1) {
      return 1.375;
    } else {
      return 1.2;
    }
  }
}
